/**
 * 🔎 usuarioFilters.js
 * Lógica pura de búsqueda, filtrado y conteo de usuarios.
 * Vive fuera del componente para poder probarse sin montar la tabla.
 */

export const ESTADO_ACTIVO = "ACTIVO";
export const ESTADO_INACTIVO = "INACTIVO";
export const ESTADO_PENDIENTE = "PENDIENTE_VERIFICACION";
export const ESTADO_BLOQUEADO = "BLOQUEADO";

export const TODOS = "TODOS";

/**
 * Un usuario sin estado se considera ACTIVO, igual que Usuario.isEnabled() en el backend.
 */
export const estadoDe = (usuario) => usuario?.estado || ESTADO_ACTIVO;

/** Quita acentos y mayúsculas para que "Perez" encuentre a "Pérez". */
const normalizar = (texto) =>
    (texto ?? "")
        .toString()
        .normalize("NFD")
        .replace(/\p{Diacritic}/gu, "")
        .toLowerCase()
        .trim();

/**
 * Busca en nombre, apellido, nombre completo, email y teléfono.
 */
export const coincideBusqueda = (usuario, termino) => {
    const q = normalizar(termino);
    if (!q) return true;

    const nombre = usuario?.nombre ?? "";
    const apellido = usuario?.apellido ?? "";

    return [nombre, apellido, `${nombre} ${apellido}`, usuario?.email, usuario?.telefono]
        .map(normalizar)
        .some((campo) => campo.includes(q));
};

export const filtrarUsuarios = (usuarios, { busqueda = "", rol = TODOS, estado = TODOS } = {}) =>
    (usuarios ?? []).filter((usuario) => {
        const pasaRol = rol === TODOS || usuario?.rolNombre === rol;
        const pasaEstado = estado === TODOS || estadoDe(usuario) === estado;
        return pasaRol && pasaEstado && coincideBusqueda(usuario, busqueda);
    });

export const contarPorEstado = (usuarios, estado) =>
    (usuarios ?? []).filter((usuario) => estadoDe(usuario) === estado).length;

export const contarPorRol = (usuarios, rol) =>
    (usuarios ?? []).filter((usuario) => usuario?.rolNombre === rol).length;

export const hayFiltrosActivos = ({ busqueda = "", rol = TODOS, estado = TODOS } = {}) =>
    busqueda.trim() !== "" || rol !== TODOS || estado !== TODOS;

/**
 * Acciones de estado disponibles para un usuario.
 *
 * - Solo se desactiva una cuenta ACTIVA que no sea ADMIN.
 * - Solo se reactiva una cuenta INACTIVA.
 * - PENDIENTE_VERIFICACION y BLOQUEADO no se resuelven desde aquí:
 *   el primero se resuelve verificando el correo, el segundo levantando la sanción.
 *
 * Refleja los guards de UsuarioServiceImpl para no ofrecer botones que darían 409.
 */
export const accionEstadoDisponible = (usuario) => {
    const estado = estadoDe(usuario);
    if (estado === ESTADO_ACTIVO) {
        return usuario?.rolNombre === "ADMIN" ? null : "DESACTIVAR";
    }
    if (estado === ESTADO_INACTIVO) {
        return "REACTIVAR";
    }
    return null;
};
